import React from "react";
import useMediaQuery from "@mui/material/useMediaQuery";
import { motion } from "framer-motion";
import {
    BarChart3, CalendarClock, Check, Cloud, Cpu, Layers, Package, Plug, ShieldCheck, Smartphone, Tag, Ticket, Users, Workflow, Wrench,
} from "lucide-react";
import { FONTS, LANDING_MOBILE } from "./landingTheme";
import { WhatsAppLogo } from "./ContactModal";

/* Conteúdo da landing: módulos e integrações do sistema (fonte: README, seção 6 e 9) */
export const SOLUTIONS = [
    {
        id: "solucao-chamados", Icon: Ticket, title: "Central de Chamados",
        short: "Defeitos registrados e atendidos até o fim",
        text: "Qualquer pessoa da loja abre o chamado com foto ou vídeo e o nível de impacto. O técnico assume e registra a solução.",
        bullets: ["Máquina em Manutenção, falha parcial ou estético", "Status: na fila, em atendimento, aguardando", "Chat disponível para comunicação com os técnicos", "Histórico com solução e peças usadas"],
    },
    {
        id: "solucao-maquinas", Icon: Wrench, title: "Ficha da Máquina",
        short: "Tudo sobre cada equipamento num só lugar",
        text: "Nº de série, patrimônio e o histórico completo de manutenções, peças aplicadas e mudanças de status.",
        bullets: ["Cada máquina com foto e manual", "Histórico de manutenções", "Auditoria de status", "Número de jogadas"],
    },
    {
        id: "solucao-preventivas", Icon: CalendarClock, title: "Preventivas",
        short: "Manutenção antes de a máquina parar",
        text: "Planos recorrentes por tempo ou por número de jogadas. O painel da loja avisa antes de vencer e destaca o que está atrasado.",
        bullets: ["Agendamento por data ou número de jogadas", "Aviso com antecedência", "Fila de prioridades por loja"],
    },
    {
        id: "solucao-estoque", Icon: Package, title: "Peças & Estoque",
        short: "Saldo por loja e rastreio de cada peça",
        text: "Entradas com nota fiscal, saídas para a máquina, balanço físico e transferência entre lojas, com alerta de estoque baixo.",
        bullets: ["Peças ligadas ao chamado de origem", "Estoque mínimo e status", "Transferência entre lojas"],
    },
    {
        id: "solucao-financeiro", Icon: BarChart3, title: "Financeiro",
        short: "Faturamento e custo de manutenção lado a lado",
        text: "Receita e jogadas por máquina e por loja, com tendência sobre o período anterior, e os custos de peças lançados na manutenção.",
        bullets: ["Faturamento diário por máquina", "Comparação entre períodos", "Custos & despesas com comprovante"],
    },
    {
        id: "solucao-equipe", Icon: Users, title: "Equipe & Permissões",
        short: "Cada pessoa vê só o que deve",
        text: "Cargos do Operador ao Proprietário, permissões por loja e por tela, convite por e-mail e registro de tudo o que foi feito.",
        bullets: ["Cargos com hierarquia", "Permissões por loja e tela", "Horário de trabalho de cada usuário", "Registro completo de atividades"],
    },
];

const STEPS = [
    { title: "Chamado aberto", text: "O operador relata o defeito com foto e escolhe o impacto." },
    { title: "Máquina em manutenção", text: "Se ela parou, sai de operação no EM e no sistema de cartão da loja automaticamente." },
    { title: "Técnico resolve", text: "Assume o chamado, registra a solução e dá baixa nas peças usadas." },
    { title: "De volta à operação", text: "A máquina volta a funcionar e tudo fica no histórico e financeiro." },
];

const INTEGRATIONS = [
    { Icon: Cpu, title: "Sistema de cartão", text: "Integração com o sistema de cartão da loja, com sincronização automática e sem digitação." },
    { Icon: Cloud, title: "Arquivos na nuvem", text: "Fotos, vídeos, manuais e notas fiscais guardados com segurança." },
    { Icon: Smartphone, title: "No celular", text: "Funciona no navegador do celular: o técnico atende o chamado na frente da máquina." },
    { Icon: ShieldCheck, title: "Dados protegidos", text: "Os dados de cada loja ficam isolados, e todo o histórico é preservado." },
];

// Revela o elemento ao entrar na tela; `from` define de onde ele vem (cada seção usa um diferente)
const reveal = (from, delay = 0) => ({
    initial: { opacity: 0, ...from },
    whileInView: { opacity: 1, x: 0, y: 0, scale: 1, rotate: 0, filter: "blur(0px)" },
    viewport: { once: false, amount: 0.2 },
    transition: { duration: 0.7, ease: [0.25, 0.4, 0.25, 1], delay },
});
const fadeUp = reveal({ y: 40, filter: "blur(6px)" });

// Rótulo com ícone, igual ao de "Equipamentos" (FeatureSection)
const SectionHeader = ({ colors, label, Icon, title, text, maxWidth = 1100 }) => (
    <motion.div {...fadeUp} style={{ textAlign: "center", maxWidth, margin: "0 auto 56px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12, fontFamily: FONTS.body, fontSize: 18, fontWeight: 700, color: colors.textDark, margin: "0 0 16px" }}>
            <Icon size={20} color={colors.textDark} /> {label}
        </div>
        <h2 style={{ fontFamily: FONTS.heading, fontSize: "clamp(28px, 4vw, 44px)", fontWeight: 800, lineHeight: 1.15, color: colors.textDark, margin: "0 0 16px", whiteSpace: "pre-line" }}>
            {title}
        </h2>
        {text && <p style={{ fontFamily: FONTS.body, fontSize: 17, lineHeight: 1.6, color: colors.textMuted, margin: 0, whiteSpace: "pre-line" }}>{text}</p>}
    </motion.div>
);

const sectionStyle = (isMobile) => ({ padding: isMobile ? "56px 12px" : "100px 24px", scrollMarginTop: 100 });
const containerStyle = { maxWidth: 1300, margin: "0 auto" };
const gridStyle = (min) => ({ display: "grid", gridTemplateColumns: `repeat(auto-fit, minmax(min(${min}px, 100%), 1fr))`, gap: 20 });
const cardStyle = (colors, darkMode) => ({
    background: darkMode ? colors.cardGrey : colors.white, borderRadius: 28, padding: 32, boxSizing: "border-box",
    border: darkMode ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(0,0,0,0.05)", scrollMarginTop: 120,
});
const iconBoxStyle = (colors, darkMode) => ({
    width: 48, height: 48, borderRadius: 14, display: "flex", alignItems: "center", justifyContent: "center",
    background: colors.primaryMain, color: darkMode ? colors.bg : colors.white, marginBottom: 20,
});

/* ─────────────────────── SOLUÇÕES ─────────────────────── */
export const SolutionsSection = ({ colors, darkMode }) => {
    const isMobile = useMediaQuery(LANDING_MOBILE);
    return (
        <section id="solucoes" style={sectionStyle(isMobile)}>
            <div style={containerStyle}>
                <SectionHeader colors={colors} label="Soluções" Icon={Layers} title="Uma plataforma para toda a operação da loja"
                    text="Do chamado aberto no salão ao faturamento da máquina, cada etapa da manutenção fica registrada." />
                {/* Celular: cards compactos em coluna (resumo de uma linha no lugar do texto longo) */}
                {/* Celular: grade 2×2 (ícone acima do título, resumo curto no lugar do texto longo) */}
                <div style={isMobile ? { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 } : gridStyle(340)}>
                    {SOLUTIONS.map(({ id, Icon, title, short, text, bullets }, i) => (
                        <motion.div key={id} id={id} {...reveal({ y: 60, scale: 0.92 }, (i % 3) * 0.12)} style={isMobile ? { ...cardStyle(colors, darkMode), padding: 12, borderRadius: 20 } : cardStyle(colors, darkMode)}>
                            <div style={isMobile ? { display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 8, marginBottom: 2 } : undefined}>
                                <div style={{ ...iconBoxStyle(colors, darkMode), ...(isMobile && { width: 36, height: 36, borderRadius: 12, marginBottom: 0 }) }}><Icon size={isMobile ? 18 : 24} /></div>
                                <h3 style={{ fontFamily: FONTS.heading, fontSize: isMobile ? 15 : 22, fontWeight: 700, color: colors.textDark, margin: isMobile ? 0 : "0 0 10px" }}>{title}</h3>
                            </div>
                            <p style={{ fontFamily: FONTS.body, fontSize: isMobile ? 12 : 15, lineHeight: 1.5, color: colors.textMuted, margin: isMobile ? "0 0 8px" : "0 0 20px" }}>{isMobile ? short : text}</p>
                            <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: isMobile ? 5 : 10 }}>
                                {bullets.map((b) => (
                                    <li key={b} style={{ display: "flex", alignItems: "center", gap: 8, fontFamily: FONTS.body, fontSize: isMobile ? 12.5 : 14, fontWeight: 600, color: colors.textDark }}>
                                        <Check size={isMobile ? 14 : 16} color={colors.success} style={{ flexShrink: 0 }} /> {b}
                                    </li>
                                ))}
                            </ul>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
};

/* ─────────────────────── COMO FUNCIONA ─────────────────────── */
export const HowItWorksSection = ({ colors, darkMode }) => {
    const isMobile = useMediaQuery(LANDING_MOBILE);
    return (
        <section id="como-funciona" style={sectionStyle(isMobile)}>
            <motion.div {...reveal({ scale: 0.9, y: 40 })} style={{ ...containerStyle, ...cardStyle(colors, darkMode), borderRadius: isMobile ? 28 : 40, padding: isMobile ? "28px 14px" : "80px 56px", boxSizing: "border-box" }}>
                <SectionHeader colors={colors} label="Como funciona" Icon={Workflow} title={"Do defeito à máquina\njogando de novo"}
                    text={<>Um fluxo único, sem planilha<br />e sem recado perdido no WhatsApp.</>} />
                {/* Celular: grade 2×2 */}
                <div style={isMobile ? { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 } : gridStyle(240)}>
                    {STEPS.map((s, i) => (
                        <motion.div key={s.title} {...reveal({ x: -60 }, 0.2 + i * 0.15)}
                            style={{ borderRadius: isMobile ? 18 : 24, padding: isMobile ? 12 : 28, background: darkMode ? "rgba(255,255,255,0.04)" : colors.bg }}>
                            <div style={{ fontFamily: FONTS.heading, fontSize: isMobile ? 22 : 40, fontWeight: 800, color: colors.primaryMain, lineHeight: 1, marginBottom: isMobile ? 6 : 16 }}>
                                {String(i + 1).padStart(2, "0")}
                            </div>
                            <h3 style={{ fontFamily: FONTS.heading, fontSize: isMobile ? 15 : 20, fontWeight: 700, color: colors.textDark, margin: isMobile ? "0 0 4px" : "0 0 8px" }}>{s.title}</h3>
                            <p style={{ fontFamily: FONTS.body, fontSize: isMobile ? 12 : 15, lineHeight: 1.5, color: colors.textMuted, margin: 0 }}>{s.text}</p>
                        </motion.div>
                    ))}
                </div>
            </motion.div>
        </section>
    );
};

/* ─────────────────────── INTEGRAÇÕES ─────────────────────── */
export const IntegrationsSection = ({ colors, darkMode }) => {
    const isMobile = useMediaQuery(LANDING_MOBILE);
    return (
        <section id="integracoes" style={sectionStyle(isMobile)}>
            <div style={containerStyle}>
                <SectionHeader colors={colors} label="Integrações" Icon={Plug} title="Tecnologia que soma à sua operação"
                    text={<>O EM se integra aos sistemas da loja<br />e trabalha junto com a sua equipe.</>} />
                {/* Celular: grade 2×2, ícone acima do título para caber na coluna estreita */}
                <div style={isMobile ? { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 } : gridStyle(220)}>
                    {INTEGRATIONS.map(({ Icon, title, text }, i) => (
                        <motion.div key={title} {...reveal({ scale: 0.6, rotate: i % 2 ? 4 : -4 }, i * 0.1)} style={isMobile ? { ...cardStyle(colors, darkMode), padding: 14, borderRadius: 20 } : cardStyle(colors, darkMode)}>
                            <div style={isMobile ? { display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 10, marginBottom: 6 } : undefined}>
                                <div style={{ ...iconBoxStyle(colors, darkMode), ...(isMobile && { width: 36, height: 36, borderRadius: 12, marginBottom: 0 }) }}><Icon size={isMobile ? 18 : 22} /></div>
                                <h3 style={{ fontFamily: FONTS.heading, fontSize: isMobile ? 15 : 19, fontWeight: 700, color: colors.textDark, margin: isMobile ? 0 : "0 0 8px" }}>{title}</h3>
                            </div>
                            <p style={{ fontFamily: FONTS.body, fontSize: isMobile ? 12 : 14, lineHeight: 1.5, color: colors.textMuted, margin: 0 }}>{text}</p>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
};

/* ─────────────────────── PREÇOS ─────────────────────── */
// Valores comerciais: altere aqui
export const PRICING = { perMachine: 2.49, storeMinimum: 50, setupFee: 100 };
const brl = (v) => v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const num = (v) => v.toLocaleString("pt-BR", { minimumFractionDigits: 2 });

const PRICING_INCLUDES = [
    "Acesso completo à plataforma",
    "Integração com o sistema de cartão da loja",
    "Gestão integrada de múltiplas lojas em uma única conta",
    "Usuários ilimitados, com controle de cargos e permissões",
    "Acesso pelo site e pelo aplicativo Android",
    "Histórico completo e auditável de todas as operações",
];

export const PricingSection = ({ colors, darkMode, onOpenAuth }) => {
    const isMobile = useMediaQuery(LANDING_MOBILE);
    const { perMachine, storeMinimum, setupFee } = PRICING;
    const terms = [
        ["Mínimo por loja", `${brl(storeMinimum)} por mês`],
        ["Implantação", `${brl(setupFee)} (taxa única)`],
    ];
    return (
        <section id="precos" style={sectionStyle(isMobile)}>
            <div style={containerStyle}>
                <SectionHeader colors={colors} label="Preço" Icon={Tag} title={"Você paga apenas\npor máquina"} />
                <motion.div {...reveal({ y: 60, scale: 0.75, filter: "blur(8px)" })} style={{ ...cardStyle(colors, darkMode), maxWidth: 560, margin: "0 auto", padding: isMobile ? 28 : 44, textAlign: "center" }}>
                    <motion.div {...reveal({ scale: 0.4 }, 0.35)} style={{
                        display: "inline-flex",
                        alignItems: "flex-start",
                        justifyContent: "center",
                        fontFamily: FONTS.heading,
                        color: colors.textDark,
                        lineHeight: 1,
                    }}>
                        <span style={{ fontSize: isMobile ? 20 : 24, fontWeight: 700, marginTop: isMobile ? 6 : 8, userSelect: "none" }}>R$</span>
                        <span style={{ fontSize: isMobile ? 64 : 80, fontWeight: 800, margin: "0 6px" }}>{num(perMachine)}</span>
                        <span style={{ fontSize: isMobile ? 20 : 24, fontWeight: 700, marginTop: isMobile ? 6 : 8, visibility: "hidden", userSelect: "none" }} aria-hidden="true">R$</span>
                    </motion.div>
                    <p style={{ fontFamily: FONTS.body, fontSize: 16, fontWeight: 600, color: colors.textMuted, margin: "8px 0 4px" }}>por máquina, por mês</p>
                    <p style={{ fontFamily: FONTS.body, fontSize: 14, fontWeight: 700, color: colors.success, margin: "0 0 28px" }}>
                        Menos da metade de uma jogada por mês
                    </p>

                    <ul style={{ listStyle: "none", padding: 0, margin: "0 0 28px", display: "flex", flexDirection: "column", gap: 12, textAlign: "left" }}>
                        {PRICING_INCLUDES.map((item, i) => (
                            <motion.li key={item} {...reveal({ x: 40 }, 0.5 + i * 0.1)} style={{ display: "flex", alignItems: "flex-start", gap: 10, fontFamily: FONTS.body, fontSize: 15, fontWeight: 600, color: colors.textDark }}>
                                <Check size={18} color={colors.success} style={{ flexShrink: 0, marginTop: 2 }} /> {item}
                            </motion.li>
                        ))}
                    </ul>

                    <div style={{ borderRadius: 18, padding: "6px 18px", marginBottom: 0, background: darkMode ? "rgba(255,255,255,0.04)" : colors.bg, textAlign: "left" }}>
                        {terms.map(([label, value], i) => (
                            <div key={label} style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 16, padding: "10px 0", borderTop: i ? "1px solid rgba(120,120,120,0.15)" : "none", fontFamily: FONTS.body, fontSize: 14 }}>
                                <span style={{ color: colors.textMuted, fontWeight: 600, flexShrink: 0 }}>{label}</span>
                                <span style={{ color: colors.textDark, fontWeight: 700, textAlign: "right" }}>{value}</span>
                            </div>
                        ))}
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

/* ─────────────────────── CHAMADA FINAL ─────────────────────── */
export const CtaSection = ({ colors, darkMode, onOpenAuth, onOpenContact }) => {
    const isMobile = useMediaQuery(LANDING_MOBILE);
    return (
        <section style={{ ...sectionStyle(isMobile), paddingTop: 0 }}>
            <motion.div {...reveal({ scale: 0.85, y: 80 })} style={{
                ...containerStyle, ...cardStyle(colors, darkMode), borderRadius: isMobile ? 28 : 40, textAlign: "center",
                padding: isMobile ? "48px 20px" : "80px 40px", boxSizing: "border-box",
            }}>
                <h2 style={{ fontFamily: FONTS.heading, fontSize: "clamp(28px, 4vw, 44px)", fontWeight: 800, color: colors.textDark, margin: "0 0 16px", lineHeight: 1.15 }}>
                    Leve o EM para a sua loja
                </h2>
                <p style={{ fontFamily: FONTS.body, fontSize: 17, color: colors.textMuted, margin: "0 auto 40px", maxWidth: 560, lineHeight: 1.6 }}>
                    Entre agora e explore a loja de demonstração, ou fale com a gente para configurar a sua.
                </p>
                <div style={{ display: "flex", gap: 16, justifyContent: "center", flexDirection: isMobile ? "column" : "row" }}>
                    <motion.button onClick={onOpenAuth} whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}
                        style={{ fontFamily: FONTS.body, fontSize: 17, fontWeight: 700, color: colors.white, background: colors.purpleDark, border: "none", borderRadius: 50, padding: "16px 36px", cursor: "pointer" }}>
                        Testar a demonstração
                    </motion.button>
                    <motion.button onClick={onOpenContact} whileHover={{ scale: 1.04, backgroundColor: "rgba(127,127,127,0.08)" }} whileTap={{ scale: 0.96 }}
                        style={{ fontFamily: FONTS.body, fontSize: 17, fontWeight: 700, color: colors.textDark, background: "transparent", border: "2px solid rgba(127,127,127,0.35)", borderRadius: 50, padding: "14px 34px", cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 10 }}>
                        <WhatsAppLogo size={20} /> Falar no WhatsApp
                    </motion.button>
                </div>
            </motion.div>
        </section>
    );
};
